// src/pages/BookingPage.jsx
// Full on-site 3-step booking — no redirections
// Step 1: Traveller details + passport
// Step 2: Payment (replace card section with Stripe/Worldpay/Opayo component)
// Step 3: Confirmation with booking reference
import React, { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const PJS = { fontFamily:"'Plus Jakarta Sans',sans-serif" };
const DS  = { fontFamily:"'DM Sans',sans-serif" };
const STEPS = ["Your Details", "Payment", "Confirmation"];

// ── Reusable field wrapper ────────────────────────────────────
function Field({ label, required, error, half, children }) {
  return (
    <div style={{
      display:"flex", flexDirection:"column", gap:5,
      flex: half ? "0 0 calc(50% - 6px)" : "0 0 100%", minWidth:0,
    }}>
      <label style={{ fontSize:13, fontWeight:700, color:"#333", ...DS }}>
        {label}{required && <span style={{ color:"#e53935" }}> *</span>}
      </label>
      {children}
      {error && <span style={{ fontSize:12, color:"#e53935", ...DS }}>{error}</span>}
    </div>
  );
}

const inputStyle = err => ({
  padding:"11px 12px",
  border:`1.5px solid ${err ? "#e53935" : "#ddd"}`,
  borderRadius:7, fontSize:15, fontFamily:"inherit",
  outline:"none", width:"100%", boxSizing:"border-box",
});

// ── Booking summary sidebar ───────────────────────────────────
function SummaryPanel({ sp }) {
  const hotel    = sp.get("hotelName") || sp.get("hotel") || "Your holiday";
  const ci       = sp.get("checkIn")   || "";
  const co       = sp.get("checkOut")  || "";
  const guests   = sp.get("guests")    || "2";
  const rooms    = sp.get("rooms")     || "1";
  const price    = Number(sp.get("price") || 0);
  const carrier  = sp.get("carrier")   || "";
  const board    = sp.get("boardBasis")|| "";
  const nights   = ci && co ? Math.max(1, Math.round((new Date(co) - new Date(ci)) / 86400000)) : 0;
  const total    = price && nights ? price * nights * Number(rooms) : price * Number(guests) || 0;
  const fd = d => d ? new Date(d).toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short",year:"numeric"}) : "";

  const rows = [
    carrier && ["Flight",    carrier],
    ci      && ["Check-in",  fd(ci)],
    co      && ["Check-out", fd(co)],
    nights  && ["Duration",  `${nights} nights`],
               ["Guests",   `${guests} guests · ${rooms} room${Number(rooms)!==1?"s":""}`],
    board   && ["Board",    board],
    price>0 && ["Per night", `£${price.toLocaleString()}`],
  ].filter(Boolean);

  return (
    <div style={{ background:"#fff", border:"1px solid #e5e5e5", borderRadius:10, overflow:"hidden", position:"sticky", top:82 }}>
      <div style={{ background:"#1565C0", color:"#fff", padding:"14px 18px" }}>
        <div style={{ fontSize:12, opacity:.8, ...DS }}>Booking summary</div>
        <div style={{ fontSize:16, fontWeight:800, ...PJS, marginTop:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {hotel}
        </div>
      </div>
      <div style={{ padding:"14px 18px" }}>
        {rows.map(([k,v]) => (
          <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid #f0f0f0", fontSize:14 }}>
            <span style={{ color:"#888", ...DS }}>{k}</span>
            <span style={{ fontWeight:600, ...DS }}>{v}</span>
          </div>
        ))}
        {total > 0 && (
          <div style={{ display:"flex", justifyContent:"space-between", padding:"12px 0 4px", fontSize:17 }}>
            <span style={{ fontWeight:800, color:"#1a1a1a", ...PJS }}>Total</span>
            <span style={{ fontWeight:900, color:"#2E7D32", fontSize:22, ...PJS }}>£{total.toLocaleString()}</span>
          </div>
        )}
        <div style={{ background:"#e8f5e9", borderRadius:7, padding:"9px 12px", marginTop:12, fontSize:13, color:"#2E7D32", fontWeight:600, ...DS }}>
          No hidden fees — all taxes included
        </div>
      </div>
      <div style={{ padding:"0 18px 16px", display:"flex", flexDirection:"column", gap:8 }}>
        <a href="tel:02030190055" style={{ display:"block", textAlign:"center", padding:10, background:"#f3f4f6", color:"#333", borderRadius:7, fontWeight:700, fontSize:14, textDecoration:"none", ...DS }}>
          📞 020 3019 0055
        </a>
        <a href="https://wa.me/447487672075" target="_blank" rel="noreferrer"
          style={{ display:"block", textAlign:"center", padding:10, background:"#25D366", color:"#fff", borderRadius:7, fontWeight:700, fontSize:14, textDecoration:"none", ...DS }}>
          WhatsApp us
        </a>
      </div>
    </div>
  );
}

// ── Step 1 — Traveller details ────────────────────────────────
function Step1({ data, setData, errors, onNext }) {
  const s = (k, v) => setData(d => ({ ...d, [k]: v }));
  return (
    <div>
      <h2 style={{ fontSize:20, fontWeight:900, marginBottom:20, ...PJS }}>Lead traveller details</h2>

      <div style={{ background:"#fff", border:"1px solid #e5e5e5", borderRadius:10, padding:22, marginBottom:16 }}>
        <h3 style={{ fontSize:16, fontWeight:800, marginBottom:16, ...PJS }}>Personal information</h3>
        <div style={{ display:"flex", flexWrap:"wrap", gap:12 }}>
          <Field label="Title" half>
            <select value={data.title} onChange={e => s("title",e.target.value)} style={inputStyle()}>
              {["Mr","Mrs","Ms","Miss","Dr","Prof"].map(t => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="First name" required error={errors.firstName} half>
            <input value={data.firstName} onChange={e=>s("firstName",e.target.value)} placeholder="John" style={inputStyle(errors.firstName)} />
          </Field>
          <Field label="Last name" required error={errors.lastName}>
            <input value={data.lastName} onChange={e=>s("lastName",e.target.value)} placeholder="Smith" style={inputStyle(errors.lastName)} />
          </Field>
          <Field label="Email address" required error={errors.email}>
            <input type="email" value={data.email} onChange={e=>s("email",e.target.value)} placeholder="john@example.com" style={inputStyle(errors.email)} />
          </Field>
          <Field label="Phone number" required error={errors.phone} half>
            <input type="tel" value={data.phone} onChange={e=>s("phone",e.target.value)} placeholder="+44 7700 000000" style={inputStyle(errors.phone)} />
          </Field>
          <Field label="Date of birth" required error={errors.dob} half>
            <input type="date" value={data.dob} onChange={e=>s("dob",e.target.value)} style={inputStyle(errors.dob)} />
          </Field>
        </div>
      </div>

      <div style={{ background:"#fff", border:"1px solid #e5e5e5", borderRadius:10, padding:22, marginBottom:16 }}>
        <h3 style={{ fontSize:16, fontWeight:800, marginBottom:16, ...PJS }}>Passport details</h3>
        <div style={{ display:"flex", flexWrap:"wrap", gap:12 }}>
          <Field label="Passport number" required error={errors.passport} half>
            <input value={data.passport} onChange={e=>s("passport",e.target.value)} placeholder="123456789" style={inputStyle(errors.passport)} />
          </Field>
          <Field label="Nationality" required half>
            <input value={data.nationality} onChange={e=>s("nationality",e.target.value)} placeholder="British" style={inputStyle()} />
          </Field>
          <Field label="Passport expiry date" required error={errors.passportExpiry}>
            <input type="date" value={data.passportExpiry} onChange={e=>s("passportExpiry",e.target.value)} style={inputStyle(errors.passportExpiry)} />
          </Field>
        </div>
      </div>

      <div style={{ background:"#fff", border:"1px solid #e5e5e5", borderRadius:10, padding:22, marginBottom:22 }}>
        <h3 style={{ fontSize:16, fontWeight:800, marginBottom:12, ...PJS }}>Special requests</h3>
        <textarea
          value={data.requests}
          onChange={e=>s("requests",e.target.value)}
          rows={3}
          placeholder="Dietary requirements, accessible room, early check-in..."
          style={{ ...inputStyle(), resize:"vertical" }}
        />
      </div>

      <button
        onClick={onNext}
        style={{ width:"100%", padding:15, background:"#2E7D32", color:"#fff", border:"none", borderRadius:8, fontSize:16, fontWeight:800, cursor:"pointer", ...PJS }}
        onMouseEnter={e => { e.currentTarget.style.background="#1B5E20"; }}
        onMouseLeave={e => { e.currentTarget.style.background="#2E7D32"; }}>
        Continue to payment
      </button>
    </div>
  );
}

// ── Step 2 — Payment ──────────────────────────────────────────
function Step2({ data, setData, errors, onNext, onBack }) {
  const s = (k, v) => setData(d => ({ ...d, [k]: v }));
  return (
    <div>
      <h2 style={{ fontSize:20, fontWeight:900, marginBottom:20, ...PJS }}>Payment details</h2>

      {/* ═══════════════════════════════════════════════════
          PAYMENT GATEWAY INTEGRATION POINT
          Replace the card fields below with your gateway:

          Stripe:
            import { PaymentElement } from "@stripe/react-stripe-js";
            <PaymentElement />

          Worldpay / Opayo / PaySafe:
            Follow their React SDK docs.
          ═══════════════════════════════════════════════════ */}

      <div style={{ background:"#e8f0fe", border:"1px solid #bbdefb", borderRadius:8, padding:"11px 15px", marginBottom:18, fontSize:14, color:"#1565C0", fontWeight:600, ...DS }}>
        🔒 Your payment is encrypted and secure. We never store your full card details.
      </div>

      <div style={{ background:"#fff", border:"1px solid #e5e5e5", borderRadius:10, padding:22, marginBottom:16 }}>
        <h3 style={{ fontSize:16, fontWeight:800, marginBottom:16, ...PJS }}>Card details</h3>
        <div style={{ display:"flex", flexWrap:"wrap", gap:12 }}>
          <Field label="Name on card" required error={errors.cardName}>
            <input value={data.cardName} onChange={e=>s("cardName",e.target.value)} placeholder="John Smith" style={inputStyle(errors.cardName)} />
          </Field>
          <Field label="Card number" required error={errors.cardNumber}>
            <input
              value={data.cardNumber}
              onChange={e=>s("cardNumber",e.target.value.replace(/\D/g,"").slice(0,16))}
              placeholder="1234 5678 9012 3456"
              style={inputStyle(errors.cardNumber)}
            />
          </Field>
          <Field label="Expiry (MM/YY)" required error={errors.expiry} half>
            <input value={data.expiry} onChange={e=>s("expiry",e.target.value)} placeholder="08/28" style={inputStyle(errors.expiry)} />
          </Field>
          <Field label="CVV" required error={errors.cvv} half>
            <input
              value={data.cvv}
              onChange={e=>s("cvv",e.target.value.replace(/\D/g,"").slice(0,4))}
              placeholder="123"
              style={inputStyle(errors.cvv)}
            />
          </Field>
        </div>
      </div>

      <div style={{ background:"#fff", border:"1px solid #e5e5e5", borderRadius:10, padding:22, marginBottom:16 }}>
        <h3 style={{ fontSize:16, fontWeight:800, marginBottom:14, ...PJS }}>Billing address</h3>
        <div style={{ display:"flex", flexWrap:"wrap", gap:12 }}>
          <Field label="Address line 1" required>
            <input value={data.addr1} onChange={e=>s("addr1",e.target.value)} placeholder="123 High Street" style={inputStyle()} />
          </Field>
          <Field label="Address line 2">
            <input value={data.addr2} onChange={e=>s("addr2",e.target.value)} placeholder="Flat 4 (optional)" style={inputStyle()} />
          </Field>
          <Field label="City" required half>
            <input value={data.city} onChange={e=>s("city",e.target.value)} placeholder="London" style={inputStyle()} />
          </Field>
          <Field label="Postcode" required half>
            <input value={data.postcode} onChange={e=>s("postcode",e.target.value)} placeholder="SW1A 1AA" style={inputStyle()} />
          </Field>
        </div>
      </div>

      <div style={{ marginBottom:20 }}>
        <label style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer", fontSize:14, color:"#333", ...DS }}>
          <input
            type="checkbox"
            checked={data.agree}
            onChange={e=>s("agree",e.target.checked)}
            style={{ marginTop:2, accentColor:"#1565C0", width:16, height:16, flexShrink:0 }}
          />
          <span>
            I agree to the <Link to="/terms" style={{ color:"#1565C0" }}>Terms &amp; Conditions</Link> and{" "}
            <Link to="/privacy" style={{ color:"#1565C0" }}>Privacy Policy</Link>.
            I confirm all passenger details are correct.
          </span>
        </label>
        {errors.agree && <div style={{ color:"#e53935", fontSize:13, marginTop:6, ...DS }}>{errors.agree}</div>}
      </div>

      <div style={{ display:"flex", gap:12 }}>
        <button
          onClick={onBack}
          style={{ padding:"15px 24px", background:"#fff", border:"1.5px solid #ddd", borderRadius:8, fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
          Back
        </button>
        <button
          onClick={onNext}
          style={{ flex:1, padding:15, background:"#2E7D32", color:"#fff", border:"none", borderRadius:8, fontSize:16, fontWeight:800, cursor:"pointer", ...PJS }}
          onMouseEnter={e=>{ e.currentTarget.style.background="#1B5E20"; }}
          onMouseLeave={e=>{ e.currentTarget.style.background="#2E7D32"; }}>
          Confirm &amp; pay
        </button>
      </div>
    </div>
  );
}

// ── Step 3 — Confirmation ─────────────────────────────────────
function Step3({ traveller, sp }) {
  const ref   = `BH${Date.now().toString().slice(-6)}`;
  const hotel = sp.get("hotelName") || sp.get("hotel") || "Your holiday";
  return (
    <div style={{ textAlign:"center", padding:"40px 0" }}>
      <div style={{
        width:72, height:72, background:"#e8f5e9", borderRadius:"50%",
        display:"flex", alignItems:"center", justifyContent:"center",
        margin:"0 auto 20px", fontSize:32, color:"#2E7D32",
      }}>✓</div>
      <h2 style={{ fontSize:26, fontWeight:900, marginBottom:8, ...PJS }}>Booking confirmed!</h2>
      <p style={{ color:"#555", fontSize:16, marginBottom:5, ...DS }}>
        Thank you, <strong>{traveller.firstName} {traveller.lastName}</strong>
      </p>
      <p style={{ color:"#555", fontSize:15, marginBottom:24, ...DS }}>
        Reference: <strong style={{ color:"#1565C0", fontSize:18, ...PJS }}>{ref}</strong>
      </p>
      <div style={{ background:"#fff", border:"1px solid #e5e5e5", borderRadius:10, padding:22, maxWidth:480, margin:"0 auto 24px", textAlign:"left" }}>
        <h3 style={{ fontSize:16, fontWeight:800, marginBottom:14, ...PJS }}>What happens next</h3>
        {[
          `Confirmation email sent to ${traveller.email}`,
          "Our team will review your booking within 2 hours",
          "Full travel documents sent 2 weeks before departure",
          "Call us anytime on 020 3019 0055",
        ].map((t, i) => (
          <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:10 }}>
            <span style={{
              width:22, height:22, background:"#e8f0fe", color:"#1565C0",
              borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:12, fontWeight:800, flexShrink:0, ...PJS,
            }}>{i+1}</span>
            <span style={{ fontSize:14, color:"#555", lineHeight:1.55, ...DS }}>{t}</span>
          </div>
        ))}
      </div>
      <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
        <Link to="/" style={{ padding:"12px 24px", background:"#1565C0", color:"#fff", borderRadius:8, fontWeight:700, fontSize:15, textDecoration:"none", ...PJS }}>
          Back to home
        </Link>
        <a
          href={`https://wa.me/447487672075?text=Booking ref: ${ref} — ${hotel}`}
          target="_blank" rel="noreferrer"
          style={{ padding:"12px 24px", background:"#25D366", color:"#fff", borderRadius:8, fontWeight:700, fontSize:15, textDecoration:"none", ...PJS }}>
          WhatsApp us
        </a>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
export default function BookingPage() {
  const [sp]  = useSearchParams();
  const [step, setStep] = useState(0);

  const [traveller, setTraveller] = useState({
    title:"Mr", firstName:"", lastName:"", email:"", phone:"",
    dob:"", passport:"", nationality:"British", passportExpiry:"", requests:"",
  });
  const [payment, setPayment] = useState({
    cardName:"", cardNumber:"", expiry:"", cvv:"",
    addr1:"", addr2:"", city:"", postcode:"", agree:false,
  });
  const [errors, setErrors] = useState({});

  const validate1 = () => {
    const e = {};
    if (!traveller.firstName.trim())    e.firstName       = "Required";
    if (!traveller.lastName.trim())     e.lastName        = "Required";
    if (!traveller.email.includes("@")) e.email           = "Valid email required";
    if (!traveller.phone.trim())        e.phone           = "Required";
    if (!traveller.dob)                 e.dob             = "Required";
    if (!traveller.passport.trim())     e.passport        = "Required";
    if (!traveller.passportExpiry)      e.passportExpiry  = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validate2 = () => {
    const e = {};
    if (!payment.cardName.trim())         e.cardName   = "Required";
    if (payment.cardNumber.length < 16)   e.cardNumber = "Enter 16-digit card number";
    if (!payment.expiry.trim())           e.expiry     = "Required";
    if (payment.cvv.length < 3)           e.cvv        = "Enter 3–4 digit CVV";
    if (!payment.agree)                   e.agree      = "You must agree to continue";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const hotel = sp.get("hotelName") || sp.get("hotel") || "Your holiday";

  return (
    <>
      <Navbar />

      {/* Progress steps */}
      <div style={{ background:"#fff", borderBottom:"1px solid #e5e5e5", padding:"0 20px" }}>
        <div style={{ maxWidth:1000, margin:"0 auto", display:"flex" }}>
          {STEPS.map((label, i) => (
            <div
              key={i}
              onClick={() => i < step && setStep(i)}
              style={{
                flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                padding:"15px 8px", fontSize:14,
                fontWeight: i === step ? 800 : 500,
                color: i < step ? "#2E7D32" : i === step ? "#1565C0" : "#aaa",
                borderBottom: i === step ? "3px solid #1565C0" : i < step ? "3px solid #2E7D32" : "3px solid transparent",
                cursor: i < step ? "pointer" : "default",
                ...DS,
              }}>
              <div style={{
                width:24, height:24, borderRadius:"50%",
                background: i < step ? "#2E7D32" : i === step ? "#1565C0" : "#e5e5e5",
                color:"#fff", display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:12, fontWeight:800, flexShrink:0, ...PJS,
              }}>
                {i < step ? "✓" : i + 1}
              </div>
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Blue header */}
      <div style={{ background:"#1565C0", padding:"18px 20px 22px" }}>
        <div style={{ maxWidth:1000, margin:"0 auto" }}>
          <nav style={{ display:"flex", gap:6, alignItems:"center", marginBottom:10, fontSize:13 }}>
            <Link to="/" style={{ color:"rgba(255,255,255,.7)", textDecoration:"none", ...DS }}>Home</Link>
            <span style={{ color:"rgba(255,255,255,.3)" }}>›</span>
            <span style={{ color:"#fff", fontWeight:600, ...DS }}>
              {step === 2 ? "Booking Confirmed" : `Booking — ${hotel}`}
            </span>
          </nav>
          {step < 2 && (
            <h1 style={{ fontSize:22, fontWeight:900, color:"#fff", margin:0, ...PJS }}>{STEPS[step]}</h1>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ background:"#f7f7f7", minHeight:"100vh", paddingBottom:100 }}>
        <div style={{
          maxWidth:1000, margin:"0 auto", padding:"24px 20px",
          display:"grid",
          gridTemplateColumns: step < 2 ? "1fr 340px" : "1fr",
          gap:24,
        }}>
          <div>
            {step === 0 && (
              <Step1
                data={traveller}
                setData={setTraveller}
                errors={errors}
                onNext={() => { if (validate1()) { setErrors({}); setStep(1); } }}
              />
            )}
            {step === 1 && (
              <Step2
                data={payment}
                setData={setPayment}
                errors={errors}
                onNext={() => { if (validate2()) { setErrors({}); setStep(2); } }}
                onBack={() => { setErrors({}); setStep(0); }}
              />
            )}
            {step === 2 && <Step3 traveller={traveller} sp={sp} />}
          </div>
          {step < 2 && <SummaryPanel sp={sp} />}
        </div>
      </div>

      <Footer />
      <style>{"@media(max-width:768px){div[style*='gridTemplateColumns']{grid-template-columns:1fr!important}}"}</style>
    </>
  );
}